import { AddMediaViaUrlNotification, UploadFileNotification } from '@stores/Notifications';
import { UploadManager } from '@stores/UploadManager/UploadManager';

export type BatchNotification = AddMediaViaUrlNotification | UploadFileNotification;

/**
 * Discrete states an upload/import batch progresses through. Transitions are
 * monotonic: once we leave 'queued' we never return; once we leave
 * 'preparing' we never return; etc. The derivation reads notification +
 * upload-manager state, so impossible combinations (e.g. wrap-up before
 * preparing) can't be expressed in the first place.
 *
 *   upload-file:        queued → preparing → uploading → wrapping-up → processing → done-*
 *   add-media-via-url:                                                  processing → done-*
 *
 * 'stalled' is the one exception to monotonicity: it sits alongside
 * 'uploading' and the two can alternate as the connection comes and goes.
 *
 * 'queued' / 'preparing' / 'uploading' / 'stalled' / 'wrapping-up' apply only
 * to upload-file batches — URL imports happen entirely server-side and have
 * no client-driven transfer phase.
 *
 * The 'queued' vs 'preparing' vs 'stalled' split — all three can show a
 * measured speed of 0, and they mean completely different things:
 *   - 'queued'     — no file in the batch has been picked up by pLimit yet
 *                    (every file is at FileUploadStatus.status === 'queued').
 *                    Typically the batch is waiting on slots held by another
 *                    concurrent batch's uploads.
 *   - 'preparing'  — at least one file has flipped to status='uploading'
 *                    (so prepareToUpload has returned), but no byte has moved
 *                    yet — bytes are about to start flowing. Visible window
 *                    depends on chunk size and connection.
 *   - 'stalled'    — bytes DID move and then stopped for longer than
 *                    BATCH_STALL_AFTER_MS. The upload is live and mid-transfer;
 *                    the connection is the problem. Detected by elapsed time
 *                    since the last advance (`isBatchStalled`), NOT by speed —
 *                    see the note at the branch itself for why speed cannot
 *                    express this.
 */
export type BatchState =
  | 'queued' // waiting for a concurrency slot, nothing started
  | 'preparing' // file picked up, no byte has moved yet
  | 'uploading' // actively transferring bytes
  | 'stalled' // bytes moved, then stopped — connection problem mid-transfer
  | 'wrapping-up' // bytes done, finishFileUpload pending
  | 'processing' // server-side processing
  | 'done-success' // every media succeeded
  | 'done-partial-failure' // some succeeded, some failed
  | 'done-total-failure' // none succeeded
  | 'cancelled-all'; // user cancelled before any success

export function deriveBatchState(n: BatchNotification, uploadManager: UploadManager): BatchState {
  // ── Terminal states first ────────────────────────────────────────────
  const failures = uploadManager.getBatchFailures(n);
  const failedCount = failures.uploadFailures.length + failures.processingFailures.length;
  const cancelledCount = failures.cancelled.length;
  const succeededCount = failures.succeededCount;

  const isDone =
    n.type === 'upload-file' ? n.uploadProgress >= 1 && n.processingProgress >= 1 : n.progress >= 1;

  if (isDone) {
    if (succeededCount === 0 && cancelledCount > 0 && failedCount === 0) {
      return 'cancelled-all';
    }
    if (failedCount > 0 && succeededCount === 0) return 'done-total-failure';
    if (failedCount > 0 && succeededCount > 0) return 'done-partial-failure';
    // Success needs something to have succeeded. A batch that was all
    // duplicates is complete (its media are already in the library), but one
    // that ended with nothing — files discarded mid-upload, cancellations the
    // user has since dismissed — is not, and used to read "Complete".
    //
    // Known issue: cancellation is recorded only in `rejectedFiles`, so a
    // cancelled batch whose rows were removed (Remove, Clear all, or a library
    // switch wiping the list) lands here and reads "Upload failed". Remove this
    // note once the batch records its cancellations the way `failedUploads`
    // records its failures.
    const skippedDuplicateCount = n.type === 'upload-file' ? (n.skippedDuplicates?.length ?? 0) : 0;
    if (succeededCount === 0 && skippedDuplicateCount === 0) return 'done-total-failure';
    return 'done-success';
  }

  // ── Active states ────────────────────────────────────────────────────
  if (n.type !== 'upload-file') {
    // URL imports skip the client-side transfer phases entirely.
    return 'processing';
  }

  const uuids = n.uploadUuids;
  const hasUploadingFile = uploadManager.hasUploadingInBatch(uuids);
  // Per-batch throughput (NOT the global-speed getBatchUploadStats, which reports
  // the whole session's speed for any batch with in-flight files). We need "is
  // THIS batch moving?" here: null ⇒ nothing started (queued), speedBps 0 ⇒ picked
  // up but no chunk yet (preparing), > 0 ⇒ transferring (uploading).
  const stats = uploadManager.getBatchActivityStats(uuids);

  // The 500ms aggregator that owns notification.uploadProgress lags the per-file
  // progress callbacks. Treat "global ETA collapsed to 0 with positive speed" as
  // an equivalent signal for "the chunks all reported 100%, the aggregator just
  // hasn't ticked yet". Without this, the state would briefly bounce back to
  // 'uploading' (or, with stats=null at the moment we're transitioning, even
  // 'queued') after we'd already crossed into wrap-up.
  const allChunksDone = n.uploadProgress >= 1 || (stats?.etaSeconds === 0 && stats.speedBps > 0);

  if (allChunksDone) {
    return hasUploadingFile ? 'wrapping-up' : 'processing';
  }

  // stats === null  ⇒ no file in the batch has uploadStartedAt set, which means
  //                   pLimit hasn't picked up any file yet. Waiting for a slot.
  if (stats === null) return 'queued';

  // Stall check comes BEFORE the speed test, and deliberately so. A stalled
  // batch does not report speedBps === 0: computeBytesBasedUploadStats anchors
  // its window at uploadStartedAt (progress 0), so windowBytes stays > 0 and the
  // speed only decays. A stalled batch therefore looks like a slow 'uploading'
  // one by speed alone, and any stall branch hung off `speedBps === 0` is dead
  // code. Elapsed-since-last-advance is the only signal that separates them.
  if (uploadManager.isBatchStalled(uuids)) return 'stalled';

  // stats with speedBps === 0  ⇒ at least one file flipped to 'uploading'
  //                              (uploadStartedAt set, elapsed > 0) but no byte
  //                              has moved yet. Genuinely still spinning up —
  //                              isBatchStalled returned false above precisely
  //                              because nothing has ever advanced.
  if (stats.speedBps === 0) return 'preparing';

  return 'uploading';
}
